import { AsyncPipe } from '@angular/common';
import { Component, forwardRef, inject, viewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterActions } from '@core/router/store';
import { select, Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ViewMainSelectorVisibilityDto, ViewTargets } from '../../../../core/services/api-clients';
import { NoEventWidgetDirective } from '../../../../core/widgets/directives/no-event-widget.directive';
import { WidgetDirective } from '../../../../core/widgets/directives/widget.directive';
import { IScriptRunnerService, SCRIPT_RUNNER_SERVICE } from '../../../../core/widgets/models/iscript-runner.service';
import { ColumnResizeEvent, ViewTab } from '../../models/view.model';
import { ViewActions } from '../../store';
import {
  LoadEntityView,
  LoadGridSettingsFromLocalStorage,
  RestoreGridSettings,
  SaveGridColumnSettings,
  SaveGridToolbarSettings,
  SaveTabsVisibilitySettings,
} from '../../store/actions';
import { getEntityView, getGridSettings } from '../../store/selectors';
import { IViewState } from '../../store/state';
import { EngineViewToolbarComponent } from '../engine-view/engine-view-toolbar/engine-view-toolbar.component';
import { EngineViewComponent } from '../engine-view/engine-view.component';
import { GridSettings } from '../engine-view/services/engine-view-settings.service';
import {
  ENGINE_VIEW_STATE_SERVICE,
  EngineFilterType,
  EngineViewStateService,
} from '../engine-view/services/engine-view-state.service';
import { EntityViewWithTabs } from '../subgrid-view/subgrid-view.component';
import { RecordsSelectorViewConfig } from './records-selector-view-config';

@Component({
  selector: 'app-records-selector-view',
  templateUrl: './records-selector-view.component.html',
  styleUrls: ['./records-selector-view.component.scss'],
  providers: [
    {
      provide: WidgetDirective,
      useExisting: forwardRef(() => RecordsSelectorViewComponent),
    },
    {
      provide: ENGINE_VIEW_STATE_SERVICE,
      useClass: EngineViewStateService,
    },
  ],
  imports: [
    MatTooltip,
    EngineViewToolbarComponent,
    EngineViewComponent,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class RecordsSelectorViewComponent extends NoEventWidgetDirective {
  stateService = inject<EngineViewStateService>(ENGINE_VIEW_STATE_SERVICE);
  private _store = inject<Store<IViewState>>(Store);
  private _dialogRef = inject<MatDialogRef<RecordsSelectorViewComponent>>(MatDialogRef);
  data = inject<RecordsSelectorViewConfig>(MAT_DIALOG_DATA);

  entityViewWithTabs$: Observable<EntityViewWithTabs>;
  settings$: Observable<GridSettings>;

  readonly engineViewComponent = viewChild(EngineViewComponent);

  get hasCustomFilters(): boolean {
    return (
      !!this.data?.query?.customFilter ||
      this.data?.query?.filter?.filters?.some((f: any) => !!f[EngineFilterType.Custom])
    );
  }

  constructor() {
    const scriptRunnerService = inject<IScriptRunnerService>(SCRIPT_RUNNER_SERVICE);

    super(undefined, scriptRunnerService);
    this.initialize(this.data.viewId);
  }

  confirm(): void {
    if (this.data.onConfirm) {
      this.data.onConfirm(this.engineViewComponent().getSelectedRecords());
    }
    this._dialogRef.close();
  }

  cancel(): void {
    if (this.data.onCancel) {
      this.data.onCancel();
    }
    this._dialogRef.close();
  }

  // handlers

  onColumnResize(event: ColumnResizeEvent) {
    this._store.dispatch(
      new SaveGridColumnSettings({
        viewOrSubgridId: event.viewId,
        settings: event,
      }),
    );
  }

  onRowDoubleClick() {
    if (this.data.selectionMode == 'single') {
      this.confirm();
    }
  }

  // Toolbar buttons handlers

  onGlobalSearchChange(globalSearch: string) {
    this.engineViewComponent().stateService.setGlobalSearch(globalSearch);
    this.engineViewComponent().refreshData();
  }

  onGlobalSearchVisibilityChange(value: boolean) {
    this.resetQuery();

    this._store.dispatch(
      new SaveGridToolbarSettings({
        viewOrSubgridId: this.data.viewId,
        settings: { isGlobalSearchEnabled: value, areFiltersEnabled: value ? false : undefined },
      }),
    );
  }

  onFiltersVisibilityChange(value: boolean) {
    this.resetQuery();

    this._store.dispatch(
      new SaveGridToolbarSettings({
        viewOrSubgridId: this.data.viewId,
        settings: { areFiltersEnabled: value, isGlobalSearchEnabled: value ? false : undefined },
      }),
    );
  }

  onViewTabsVisibilityChange(viewTabs: ViewTab[]) {
    const selectedViewTab = viewTabs.find((x) => x.isVisible);
    if (selectedViewTab.viewId !== this.data.viewId) {
      this.initialize(selectedViewTab.viewId);

      this._store.dispatch(
        new SaveTabsVisibilitySettings({
          entityId: this.engineViewComponent().entityView.view.entity.id,
          tabsVisibilitySettings: viewTabs.map((viewTab) =>
            ViewMainSelectorVisibilityDto.fromJS({
              viewId: viewTab.viewId,
              isVisibleOnMainSelector: viewTab.isVisible,
            }),
          ),
        }),
      );
    }
  }

  onRefreshData() {
    this.engineViewComponent().refreshData();
  }

  onRestoreSettings() {
    this._store.dispatch(new RestoreGridSettings({ viewOrSubgridId: this.data.viewId }));
  }

  onNavigateToSettings() {
    this._store.dispatch(new RouterActions.NavigateToViewSettings({ viewId: this.engineViewComponent().getViewId() }));
  }

  onPageSizeChange(pageSize: number) {
    this._store.dispatch(
      new ViewActions.SetUserViewPageSize({
        viewId: this.engineViewComponent().getViewId(),
        pageSize: pageSize,
      }),
    );
  }

  // private methods

  initialize(viewId: string) {
    this.data.viewId = viewId;

    this._store.dispatch(new LoadEntityView({ viewId, viewTarget: ViewTargets.Expander }));
    this._store.dispatch(new LoadGridSettingsFromLocalStorage({ viewOrSubgridId: viewId }));

    this.entityViewWithTabs$ = this._store.pipe(
      select(getEntityView(viewId)),
      map((entityView) => {
        if (entityView == null) return null;

        const viewTabs = entityView.simpleViews
          .filter((x) => (x.viewTargets & ViewTargets.Expander) == ViewTargets.Expander)
          .map((x) => {
            return <ViewTab>{
              viewId: x.id,
              label: x.label,
              isVisible: viewId === x.id,
            };
          });

        if (viewTabs.find((x) => x.isVisible) == null) {
          viewTabs[0].isVisible = true;
        }

        return <EntityViewWithTabs>{
          entityView: entityView,
          viewTabs: viewTabs,
        };
      }),
      tap((_) => {
        if (this.data.query) {
          this.stateService.setSystemQuery(this.data.query);
        } else {
          this.stateService.setSystemQuery(undefined);
        }
      }),
    );

    this.settings$ = this._store.select(getGridSettings(viewId));
  }

  private resetQuery() {
    const engineViewComponent = this.engineViewComponent();
    if (engineViewComponent.stateService.resetQuery()) {
      engineViewComponent.refreshData();
    }
  }
}
